import { applyTheme } from '@amzn/awsui-components-core/theming';
import { KatSpinner } from '@amzn/katal-react';
import defaultStencilTokens from '@amzn/stencil-design-tokens/js/web/theme/stencilTheme';
import { StencilProvider, ThemeMode } from '@amzn/stencil-react-components/context';
import React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { App } from './components/App';
import AppInitWrapper from './components/AppInitWrapper';
import { ThemeContextProvider } from './components/context/ThemeContextProvider';
import { UserPreferences, resolveFontStack } from 'src/components/dfp-home-page/top-navigation/user-preferences/user-preferences.types';
import { eLocalStorageKeys } from './constants/AppConstants';
import { APP_THEME } from './constants/ThemeConstants';
import { usePersistedState } from './hooks/useLocalStorage';
import { logger } from 'src/logger';

import './index.scss';

// Apply theme before React mounts to prevent FOUC (flash of unstyled content)
applyTheme({ theme: APP_THEME });

// Apply the persisted font family before React's first paint so the initial
// render (including Stencil avatars, which resolve --font-family-base via the
// theme token below) already uses the final font — avoids a post-load reflow.
// Mirrors the pre-mount applyTheme call above. useUserPreferences keeps the var
// in sync afterward for live preference changes.
const applyPersistedFont = (): void => {
  try {
    const stored = localStorage.getItem(eLocalStorageKeys.UserPreferences);
    const fontFamily = stored ? (JSON.parse(stored) as Partial<UserPreferences>).fontFamily : undefined;
    document.documentElement.style.setProperty('--font-family-base', resolveFontStack(fontFamily));
  } catch (err) {
    // Always set the var to the default so first paint is never left waiting on
    // React/useUserPreferences to set it (which would reintroduce the reflow).
    document.documentElement.style.setProperty('--font-family-base', resolveFontStack(undefined));
    logger.error('Failed to apply persisted font preference before paint', {
      error: err instanceof Error ? err.message : String(err)
    });
  }
};
applyPersistedFont();

// Block third-party Chrome extension overlays (config-driven via SSM)
import { initExtensionBlocker } from 'src/utils/extensionBlocker';
initExtensionBlocker();

// Drive Stencil's font token from the shared --font-family-base CSS variable so
// Stencil components (e.g. avatars) follow the same font as the rest of the app
// and the user's font preference, instead of being pinned to a literal family.
const STENCIL_THEME = {
  ...defaultStencilTokens,
  ':root': {
    ...defaultStencilTokens[':root'],
    '--st-dt-text-family-default': 'var(--font-family-base)'
  }
};

export const StencilWithTheme: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Use the same localStorage key as DFPTopNavigation
  const [isDarkModeEnabled] = usePersistedState(eLocalStorageKeys.IsDarkModeEnabled, false);
  const themeMode = isDarkModeEnabled ? ThemeMode.Dark : ThemeMode.Light;
  return (
    <StencilProvider themeMode={themeMode} theme={STENCIL_THEME}>
      {children}
    </StencilProvider>
  );
};

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOMClient.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ThemeContextProvider>
        <StencilWithTheme>
          <AppInitWrapper>
            <React.Suspense fallback={<KatSpinner size="large" />}>
              <App />
            </React.Suspense>
          </AppInitWrapper>
        </StencilWithTheme>
      </ThemeContextProvider>
    </React.StrictMode>
  );
}
